import { useState, useEffect } from "react";
import { X } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Link } from "react-router-dom";
import { initTracking, revokeTracking } from "@/lib/tracking";

const STORAGE_KEY = "cookie-consent";

function readConsent(): string | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return stored;
  } catch {
    /* localStorage unavailable */
  }
  const match = document.cookie.match(/(?:^|;\s*)cookie-consent=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

function writeConsent(value: string) {
  try {
    localStorage.setItem(STORAGE_KEY, value);
  } catch {
    /* localStorage unavailable */
  }
  document.cookie = `${STORAGE_KEY}=${value}; path=/; max-age=${60 * 60 * 24 * 365}; SameSite=Lax`;
}

export function CookieBanner() {
  const { t, localizedPath } = useLanguage();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const consent = readConsent();
    if (!consent) {
      setVisible(true);
    } else if (consent === "accepted") {
      initTracking();
    }
  }, []);

  const accept = () => {
    writeConsent("accepted");
    setVisible(false);
    initTracking();
  };

  const reject = () => {
    writeConsent("rejected");
    setVisible(false);
    revokeTracking();
  };


  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[90] bg-background text-foreground border-t border-border px-6 py-5 animate-fade-up">
      <div className="max-w-[1400px] mx-auto flex flex-col md:flex-row items-start md:items-center gap-5 md:gap-8">
        {/* Text block */}
        <div className="flex-1 text-xs leading-relaxed">
          <p>
            {t("cookie.text")}{" "}
            <Link
              to={localizedPath("/datenschutz")}
              className="underline underline-offset-2 hover:opacity-80 transition-opacity uppercase tracking-widest font-medium"
            >
              {t("cookie.link")}
            </Link>
          </p>
        </div>

        {/* Buttons */}
        <div className="flex items-center gap-3 flex-shrink-0">
          <button
            onClick={reject}
            className="rounded-full px-5 py-2.5 text-[11px] uppercase tracking-widest border border-accent text-accent-foreground hover:bg-accent/10 transition-colors"
          >
            {t("cookie.reject")}
          </button>
          <button
            onClick={accept}
            className="rounded-full px-5 py-2.5 text-[11px] uppercase tracking-widest bg-accent text-accent-foreground font-medium hover:opacity-90 transition-opacity"
          >
            {t("cookie.accept")}
          </button>
          <button
            onClick={reject}
            className="ml-1 p-1.5 text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
